<template>
  <div class="app-layout">
    <Sidebar @toggle-sidebar="toggleSidebar" :class="{ 'sidebar-open': isSidebarOpen }" />
    <div class="main-content">
      <div class="terms-container">
        <div class="page-header">
          <div class="header-content">
            <div class="title-wrapper">
              <h1 class="page-title">{{ terms.title }}</h1>
              <div class="version-badge">Version {{ terms.version }}</div>
            </div>
            <p class="page-subtitle">Last Updated: {{ formatDate(terms.last_updated) }}</p>
          </div>
        </div>

        <div class="content-card">
          <div class="terms-content" v-if="terms.content">
            <div v-for="(section, index) in parsedContent" :key="index" class="terms-section">
              <h2 v-if="section.title" class="section-title">{{ section.title }}</h2>
              <div v-html="formatContent(section.content)" class="section-content"></div>
            </div>
          </div>
          <div v-else class="loading-content">
            <div class="spinner-wrapper">
              <svg class="spinner" viewBox="0 0 50 50">
                <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
              </svg>
            </div>
            <span>Loading terms...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'TermsOfService',
  components: {
    Sidebar
  },
  data() {
    return {
      terms: {
        title: '',
        last_updated: '',
        version: '',
        content: ''
      },
      isSidebarOpen: false
    };
  },
  computed: {
    parsedContent() {
      if (!this.terms.content) return [];

      // Split content into sections
      const sections = this.terms.content.split(/(?=\d+\.\s+)/);
      
      return sections.map(section => {
        const lines = section.trim().split('\n');
        const titleMatch = lines[0].match(/^\d+\.\s+(.+)$/);
        
        if (titleMatch) {
          return {
            title: titleMatch[1],
            content: lines.slice(1).join('\n').trim()
          };
        }
        
        return {
          title: '',
          content: section.trim()
        };
      });
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    formatContent(content) {
      // Convert line breaks to paragraphs and preserve formatting
      return content
        .split('\n')
        .filter(line => line.trim())
        .map(line => `<p>${line}</p>`)
        .join('');
    },
    async fetchTerms() {
      try {
        const response = await axios.get('https://api.kewaar.com/api/terms');
        this.terms = response.data;
      } catch (error) {
        console.error('Error fetching terms:', error);
      }
    }
  },
  created() {
    this.fetchTerms();
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: #F7FAFC;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
}

.terms-container {
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1A202C;
  margin: 0;
  line-height: 1.2;
}

.version-badge {
  background: #EBF8FF;
  color: #2B6CB0;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}

.page-subtitle {
  color: #718096;
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
}

.content-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  padding: 2rem;
}

.terms-content {
  max-width: 800px;
  margin: 0 auto;
}

.terms-section {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2D3748;
  margin: 0 0 1rem;
}

.section-content {
  color: #4A5568;
  line-height: 1.6;
}

.section-content p {
  margin: 0.75rem 0;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #718096;
}

.spinner-wrapper {
  background: #EBF8FF;
  padding: 1rem;
  border-radius: 50%;
}

.spinner {
  animation: rotate 2s linear infinite;
  width: 32px;
  height: 32px;
}

.spinner .path {
  stroke: #4299E1;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

@media (max-width: 1024px) {
  .main-content {
    padding: 1.5rem;
  }

  .terms-container {
    max-width: 100%;
  }

  .content-card {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .title-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .version-badge {
    align-self: flex-start;
  }

  .content-card {
    padding: 1rem;
    border-radius: 12px;
    margin: 0 -1rem;
  }
}
</style> 